/**
 * Applicant Form_Renderer — PhaseDeadlineBanner
 * (season-phase-deadlines spec, task 15).
 *
 * The applicant-facing display of a Season's phase deadline plus the specific
 * phase-closed / locked notice, rendered above the dynamic form sections in the
 * profile/application area. It is purely presentational — the enclosing
 * container decides which deadline applies (the Phase_1_Close_Date on the
 * Phase 1 surface, or the Phase_2_Due_Date on an APPROVED Application's Phase 2
 * surface) and mirrors the server-derived lock state — this component only
 * renders what it is handed.
 *
 *   - `deadline` (an absolute ISO-8601 instant) renders through the shared
 *     `formatDeadlineLocal` in the viewer's local timezone; an absent/empty
 *     deadline renders nothing (Req 8.1, 8.2, 8.3, 8.4, 8.5, 13.1–13.5).
 *   - `lock` (a `PhaseLockState`, or `null` when open) renders the specific
 *     phase-and-Season closed/locked message rather than a generic error
 *     (Req 9.1, 9.3, 9.5, 16.5, 17.1).
 *
 * Validates: Requirements 8.1, 8.2, 8.3, 8.4, 8.5, 9.1, 9.3, 9.5, 13.1, 13.2,
 *   13.3, 13.4, 13.5, 16.5, 17.1
 */

import * as React from 'react';

import { formatDeadlineLocal } from '@/lib/phaseDeadlineFormat';

import type { PhaseLockState } from './validationMessaging';

export interface PhaseDeadlineBannerProps {
  /**
   * The deadline to display for the current surface (Phase_1_Close_Date on the
   * Phase 1 surface, Phase_2_Due_Date on an APPROVED Phase 2 surface). An
   * absent / empty / unparseable value renders no deadline (Req 8.2, 8.4, 8.5).
   */
  deadline?: string | null;
  /** The deadline's phase label, e.g. `Phase 1 closes` / `Phase 2 due`. */
  deadlineLabel?: string;
  /**
   * The mirrored lock/closed state, or `null` when the Application is open. When
   * present its specific phase-and-Season message is surfaced (Req 9.5).
   */
  lock?: PhaseLockState | null;
}

export function PhaseDeadlineBanner({
  deadline,
  deadlineLabel = 'Deadline',
  lock,
}: PhaseDeadlineBannerProps): React.ReactElement | null {
  // The deadline is shown as the wall-clock date/time in the viewer's local zone
  // (Req 7.6, 13.5); an absent deadline yields '' and is omitted entirely.
  const formatted = formatDeadlineLocal(deadline, { withZone: true });

  if (!formatted && !lock) {
    return null;
  }

  return (
    <div className="space-y-3">
      {formatted ? (
        <p
          data-testid="phase-deadline"
          className="text-body-sm text-muted-foreground"
        >
          <span className="font-medium text-foreground">{deadlineLabel}:</span> {formatted}
        </p>
      ) : null}

      {lock ? (
        <div
          role="status"
          data-testid="phase-lock-notice"
          data-lock-kind={lock.kind}
          className="rounded-input border border-warning/40 bg-warning-muted p-4 text-body-sm text-warning-text"
        >
          {lock.message}
        </div>
      ) : null}
    </div>
  );
}

export default PhaseDeadlineBanner;
